import { getLocale, translate, translateRich, useLocale } from "../localization";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import ExperiencesAPI from "../services/ExperiencesAPI";
import { canCustomizeJoinCodeExpiry } from "../utility/accountRoles";
import { AppButton, AppTextField } from "./ui";
import { useAuth } from "../context/AuthContext";
import "./JoinCodeDialog.scss";

const DEFAULT_EXPIRY_DAYS = 1;
const HOURS_PER_DAY = 24;
const DEFAULT_EXPIRY_HOURS = DEFAULT_EXPIRY_DAYS * HOURS_PER_DAY;

const getApiResponseData = (response) => response?.data?.data ?? response?.data ?? null;

const formatDateTime = (value) => {
  if (!value) {
    return translate("Unknown");
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return translate("Unknown");
  }

  return parsedDate.toLocaleString(getLocale());
};

const getJoinCodeStatus = (joinCode, now = Date.now()) => {
  const persistedStatus = String(joinCode?.status ?? "ACTIVE").trim().toUpperCase();

  if (persistedStatus && persistedStatus !== "ACTIVE") {
    return persistedStatus;
  }

  const expiresAtMs = new Date(joinCode?.expiresAt).getTime();
  if (Number.isFinite(expiresAtMs) && expiresAtMs <= now) {
    return "EXPIRED";
  }

  return "ACTIVE";
};

const getJoinCodeStatusColor = (status) => {
  if (status === "ACTIVE") {
    return "success";
  }

  if (status === "EXPIRED") {
    return "warning";
  }

  return "default";
};

const sortJoinCodes = (joinCodes) =>
  [...joinCodes].sort((left, right) => {
    const leftCreatedAt = new Date(left?.createdAt || 0).getTime();
    const rightCreatedAt = new Date(right?.createdAt || 0).getTime();
    return rightCreatedAt - leftCreatedAt;
  });

const getCustomExpiryDaysError = (value) => {
  const trimmedValue = String(value ?? "").trim();
  if (!trimmedValue) {
    return translate("Enter a duration.");
  }

  const parsedValue = Number(trimmedValue);
  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return translate("Use a number greater than 0.");
  }

  return "";
};

const getExpiryHoursFromDays = (value) =>
  Math.round(Number(value) * HOURS_PER_DAY);

const JoinCodeDialog = ({ open, onClose, experienceId, experienceName }) => {
  const locale = useLocale();
  const { permissions, roleName } = useAuth();
  const canManageJoinCodes = permissions?.manageJoinCodes === true;
  const [joinCodes, setJoinCodes] = useState([]);
  const [now, setNow] = useState(Date.now);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [customExpiryDays, setCustomExpiryDays] = useState(
    String(DEFAULT_EXPIRY_DAYS),
  );
  const canCustomizeExpiry = canCustomizeJoinCodeExpiry(roleName);
  const customExpiryError = canCustomizeExpiry
    ? getCustomExpiryDaysError(customExpiryDays)
    : "";

  const loadJoinCodes = useCallback(async () => {
    if (!experienceId) {
      return;
    }

    setLoading(true);
    setJoinCodes([]);
    setErrorMessage("");

    try {
      const response = await ExperiencesAPI.get(
        `experiences/${experienceId}/join-codes`,
        { requiresAuth: true },
      );
      setJoinCodes(sortJoinCodes(getApiResponseData(response) || []));
    } catch (error) {
      console.error("Failed to load join codes", error);
      setErrorMessage(
        error?.response?.data?.message ||
          translate("Unable to load join codes for this experience."),
      );
    } finally {
      setLoading(false);
    }
  }, [experienceId, locale]);

  useEffect(() => {
    if (!open || !experienceId || !canManageJoinCodes) {
      return;
    }

    loadJoinCodes();
  }, [experienceId, canManageJoinCodes, loadJoinCodes, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setNow(Date.now());
    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setErrorMessage("");
      setSuccessMessage("");
      setCustomExpiryDays(String(DEFAULT_EXPIRY_DAYS));
    }
  }, [open]);

  const displayedJoinCodes = useMemo(
    () =>
      (canManageJoinCodes ? joinCodes : []).map((joinCode) => ({
        ...joinCode,
        status: getJoinCodeStatus(joinCode, now),
      })),
    [joinCodes, now, canManageJoinCodes, locale],
  );

  const activeJoinCode = useMemo(
    () => displayedJoinCodes.find((joinCode) => joinCode.status === "ACTIVE") || null,
    [displayedJoinCodes, locale],
  );

  const handleCreateJoinCode = async () => {
    if (!experienceId) {
      return;
    }

    if (!canManageJoinCodes) {
      setErrorMessage(translate("Upgrade required before you can generate join codes."));
      return;
    }

    const expiresInHours = canCustomizeExpiry
      ? getExpiryHoursFromDays(customExpiryDays)
      : DEFAULT_EXPIRY_HOURS;

    if (canCustomizeExpiry && customExpiryError) {
      setErrorMessage(customExpiryError);
      return;
    }

    setCreating(true);
    setErrorMessage("");

    try {
      const response = await ExperiencesAPI.post(
        `experiences/${experienceId}/join-codes`,
        { expiresInHours },
        { requiresAuth: true },
      );
      const createdJoinCode = getApiResponseData(response);

      setJoinCodes((currentJoinCodes) =>
        sortJoinCodes([
          createdJoinCode,
          ...currentJoinCodes.filter(
            (existingJoinCode) => existingJoinCode.id !== createdJoinCode.id,
          ),
        ]),
      );
      setSuccessMessage(translate("Join code created."));
    } catch (error) {
      console.error("Failed to create join code", error);
      setErrorMessage(
        error?.response?.data?.message ||
          translate("Unable to create a join code right now."),
      );
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeJoinCode = async (joinCodeId) => {
    if (!joinCodeId) {
      return;
    }

    if (!canManageJoinCodes) {
      return;
    }

    setRevokingId(joinCodeId);
    setErrorMessage("");

    try {
      const response = await ExperiencesAPI.post(
        `join-codes/${joinCodeId}/revoke`,
        {},
        { requiresAuth: true },
      );
      const revokedJoinCode = getApiResponseData(response);

      setJoinCodes((currentJoinCodes) =>
        sortJoinCodes(
          currentJoinCodes.map((joinCode) =>
            joinCode.id === revokedJoinCode.id ? revokedJoinCode : joinCode,
          ),
        ),
      );
      setSuccessMessage(translate("Join code revoked."));
    } catch (error) {
      console.error("Failed to revoke join code", error);
      setErrorMessage(
        error?.response?.data?.message ||
          translate("Unable to revoke this join code right now."),
      );
    } finally {
      setRevokingId("");
    }
  };

  const handleCopyValue = async (label, value) => {
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setSuccessMessage(translate("{value0} copied.", {
  value0: label
}));
    } catch (error) {
      console.error(`Failed to copy ${label}`, error);
      setErrorMessage(translate("Unable to copy {value0}.", {
  value0: label.toLowerCase()
}));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={creating || Boolean(revokingId) ? undefined : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>{translate("Join Codes")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography color="text.secondary">{translateRich("Generate a join code for {value0}. Students can scan the QR code, enter the short code, or use the join link.", {
  value0: experienceName || translate("this experience")
})}</Typography>

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {successMessage && <Alert severity="success">{successMessage}</Alert>}
          {!canManageJoinCodes && (
            <Alert
              severity="warning"
              action={
                <AppButton component={Link} to="/upgrade" kind="secondary" onClick={onClose}>{translate("Upgrade")}</AppButton>
              }
            >{translate("Join codes are not included with your current account access.")}</Alert>
          )}

          <Box className="joinCodeDialog__active">
            <Stack spacing={1.5}>
              <Box className="joinCodeDialog__header">
                <Box className="joinCodeDialog__details">
                  {activeJoinCode ? (
                    <>
                      <Typography variant="overline" sx={{ opacity: 0.72 }}>{translate("Current Active Code")}</Typography>
                      <Box component="h4" className="joinCodeDialog__code">
                        {activeJoinCode.code}
                      </Box>
                      <Typography variant="body2" color="text.secondary" className="joinCodeDialog__expiry">{translateRich("Expires: {value0}", {
  value0: formatDateTime(activeJoinCode.expiresAt)
})}</Typography>
                    </>
                  ) : (
                    <Stack spacing={0.5}>
                      <Box component="h4" className="joinCodeDialog__emptyTitle">
                        {loading ? translate("Loading join codes...") : translate("No active join code")}
                      </Box>
                      {!loading && canManageJoinCodes && (
                        <Typography variant="body2" color="text.secondary">{translate("Generate a code below for students to join.")}</Typography>
                      )}
                    </Stack>
                  )}
                </Box>
                {canManageJoinCodes && !loading && activeJoinCode?.joinUrl && (
                  <Box className="joinCodeDialog__qr">
                    <QRCodeSVG
                      value={activeJoinCode.joinUrl}
                      size={288}
                      marginSize={4}
                      level="M"
                      role="img"
                      title={translate("Scan to join")}
                    />
                    <Typography variant="body2" color="text.secondary">{translate("Scan to join")}</Typography>
                  </Box>
                )}
              </Box>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "flex-start" }}
              >
                {canCustomizeExpiry && (
                  <AppTextField
                    label={translate("Duration")}
                    type="number"
                    value={customExpiryDays}
                    onChange={(event) => setCustomExpiryDays(event.target.value)}
                    disabled={creating || !canManageJoinCodes}
                    error={Boolean(customExpiryError)}
                    helperText={customExpiryError || translate("Days")}
                    inputProps={{
                      min: 1,
                      step: 1,
                    }}
                    sx={{ width: { xs: "100%", sm: 140 } }}
                  />
                )}
                <AppButton
                  kind="secondary"
                  startIcon={<RefreshOutlinedIcon />}
                  onClick={handleCreateJoinCode}
                  disabled={
                    creating ||
                    !canManageJoinCodes ||
                    Boolean(customExpiryError)
                  }
                >
                  {creating ? translate("Generating...") : activeJoinCode ? translate("Generate New") : translate("Generate")}
                </AppButton>
              </Stack>

              {activeJoinCode?.joinUrl && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <AppButton
                    kind="secondary"
                    startIcon={<ContentCopyOutlinedIcon />}
                    onClick={() => handleCopyValue(translate("Join code"), activeJoinCode.code)}
                  >{translate("Copy Code")}</AppButton>
                  <AppButton
                    kind="secondary"
                    startIcon={<LinkOutlinedIcon />}
                    onClick={() => handleCopyValue(translate("Join link"), activeJoinCode.joinUrl)}
                  >{translate("Copy Link")}</AppButton>
                  <AppButton
                    kind="ghost"
                    startIcon={<BlockOutlinedIcon />}
                    onClick={() => handleRevokeJoinCode(activeJoinCode.id)}
                    disabled={revokingId === activeJoinCode.id}
                  >
                    {revokingId === activeJoinCode.id ? translate("Revoking...") : translate("Revoke")}
                  </AppButton>
                </Stack>
              )}
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant="overline" sx={{ opacity: 0.72 }}>{translate("Recent Codes")}</Typography>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : displayedJoinCodes.length > 0 ? (
              <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                {displayedJoinCodes.map((joinCode) => (
                  <Box
                    key={joinCode.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography variant="h6" sx={{ letterSpacing: "0.12em" }}>
                          {joinCode.code}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                          <Chip
                            size="small"
                            label={joinCode.status}
                            color={getJoinCodeStatusColor(joinCode.status)}
                          />
                          <Typography variant="body2" color="text.secondary">{translateRich("Expires {value0}", {
  value0: formatDateTime(joinCode.expiresAt)
})}</Typography>
                        </Stack>
                      </Box>
                      <Typography variant="body2" color="text.secondary">{translateRich("Redeemed {value0} times", {
  value0: joinCode.redeemCount || 0
})}</Typography>
                    </Stack>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      sx={{ mt: 1.5 }}
                    >
                      <AppButton
                        kind="secondary"
                        startIcon={<ContentCopyOutlinedIcon />}
                        onClick={() => handleCopyValue(translate("Join code"), joinCode.code)}
                      >{translate("Copy Code")}</AppButton>
                      <AppButton
                        kind="secondary"
                        startIcon={<LinkOutlinedIcon />}
                        onClick={() => handleCopyValue(translate("Join link"), joinCode.joinUrl)}
                      >{translate("Copy Link")}</AppButton>
                      {joinCode.status === "ACTIVE" && (
                        <AppButton
                          kind="ghost"
                          startIcon={<BlockOutlinedIcon />}
                          onClick={() => handleRevokeJoinCode(joinCode.id)}
                          disabled={revokingId === joinCode.id}
                        >
                          {revokingId === joinCode.id ? translate("Revoking...") : translate("Revoke")}
                        </AppButton>
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography sx={{ mt: 1.5 }} color="text.secondary">{translate("No join codes generated yet.")}</Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <AppButton kind="ghost" onClick={onClose} disabled={creating || Boolean(revokingId)}>{translate("Close")}</AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default JoinCodeDialog;
